import { Field, useField } from 'formik';
import { memo, useMemo } from 'react';

import { BaseTextFieldProps, Box, Divider, Grid, TextField, Typography } from '@mui/material';

import LanguageTabs from './LanguageTabs';
import QuestionTypeSelect from './QuestionTypeSelect';
import { Question, QuestionType } from '@synergetx/puppet-interview-types';

interface Props {
    position: number;
    toggleEditing: () => void;
}

function QuestionForm({ position, toggleEditing }: Props) {
    const [{ value: question }] = useField<Question>(`questions[${position}]`);

    const fieldInputProps: BaseTextFieldProps = useMemo(
        () => ({
            style: { minWidth: '20rem' },
            error: isInError(question?.name),
            helperText: getHelperText('Question name', question?.name),
            onFocus: (e) => {
                e.target.select();
            },
            onClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
            },
        }),
        [question?.name],
    );

    const isPuppetQuestion = question?.type === QuestionType.PUPPET;

    return (
        <Box sx={{ mt: 0.5 }}>
            {/* Header row: Question number + name input */}
            <Box
                onClick={toggleEditing}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                    {`Question ${position + 1}`}
                </Typography>
                <Field
                    key={`question.name - ${position}`}
                    as={TextField}
                    required
                    name={`questions[${position}].name`}
                    label="Question Name"
                    variant="outlined"
                    size="small"
                    placeholder="Enter a question name..."
                    slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    sx={{ minWidth: '16rem', flex: '1 1 auto', maxWidth: '24rem' }}
                />
            </Box>

            {/* Question Type and Settings */}
            <Grid container spacing={2}>
                <QuestionTypeSelect position={position} value={question?.type} />
            </Grid>

            {/* Divider before content fields */}
            {isPuppetQuestion && (
                <Divider sx={{ my: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        Puppet Scripts
                    </Typography>
                </Divider>
            )}

            {/* Language tabs / content fields */}
            <Box sx={{ mt: isPuppetQuestion ? 0 : 2 }}>
                <LanguageTabs position={position} />
            </Box>
        </Box>
    );
}

// TODO: ESLint should recommend removal from React Function component - see puppet app lint rules
function isInError(option: string | undefined) {
    if (option === undefined) {
        return true;
    }

    return option.trim().length === 0;
}

/**
 * Returns helper text for TextField validation.
 *
 * @param {string} value
 */
function getHelperText(name: string, value: string | undefined) {
    if (value === undefined) {
        return '';
    }

    return value.trim().length === 0 ? `${name} cannot be empty` : '';
}

export default memo(QuestionForm);
